import { HEX_IMAGE_HEIGHT, HEX_IMAGE_WIDTH } from "@west-marches/shared";
import type { AdminCreateOverlayAsset } from "@/app/admin/adminTypes";

export interface AdminCreateLayer {
  id: string;
  overlayId: string;
  filename: string;
  url: string;
  x: number;
  y: number;
  scale: number;
  opacity: number;
}

export const ADMIN_CREATE_CANVAS_WIDTH = HEX_IMAGE_WIDTH;
export const ADMIN_CREATE_CANVAS_HEIGHT = HEX_IMAGE_HEIGHT;
export const ADMIN_CREATE_MIN_SCALE = 0.2;
export const ADMIN_CREATE_MAX_SCALE = 4;

function createLayerId(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `layer-${Math.random().toString(36).slice(2, 10)}`;
}

export function clampAdminCreateScale(scale: number): number {
  return Math.min(ADMIN_CREATE_MAX_SCALE, Math.max(ADMIN_CREATE_MIN_SCALE, scale));
}

export function createAdminCreateLayer(asset: AdminCreateOverlayAsset): AdminCreateLayer {
  return {
    id: createLayerId(),
    overlayId: asset.id,
    filename: asset.filename,
    url: asset.url,
    x: 0,
    y: 0,
    scale: 1,
    opacity: 1,
  };
}

export function duplicateAdminCreateLayer(layer: AdminCreateLayer): AdminCreateLayer {
  return {
    ...layer,
    id: createLayerId(),
    x: layer.x + 12,
    y: layer.y + 12,
  };
}

export function prepareAdminCreateComposeTransition(input: {
  layers: AdminCreateLayer[];
  selectedOverlays: AdminCreateOverlayAsset[];
}): {
  layers: AdminCreateLayer[];
  selectedLayerId: string | null;
  addedOverlayCount: number;
} {
  if (input.selectedOverlays.length === 0) {
    return {
      layers: input.layers,
      selectedLayerId: input.layers.at(-1)?.id ?? null,
      addedOverlayCount: 0,
    };
  }

  const nextLayers = input.selectedOverlays.map((overlay) => createAdminCreateLayer(overlay));
  return {
    layers: [...input.layers, ...nextLayers],
    selectedLayerId: nextLayers.at(-1)?.id ?? null,
    addedOverlayCount: nextLayers.length,
  };
}

export function moveLayerBackward(layers: AdminCreateLayer[], layerId: string): AdminCreateLayer[] {
  const index = layers.findIndex((layer) => layer.id === layerId);
  if (index <= 0) {
    return layers;
  }
  const next = [...layers];
  const [layer] = next.splice(index, 1);
  if (!layer) {
    return layers;
  }
  next.splice(index - 1, 0, layer);
  return next;
}

export function moveLayerForward(layers: AdminCreateLayer[], layerId: string): AdminCreateLayer[] {
  const index = layers.findIndex((layer) => layer.id === layerId);
  if (index === -1 || index >= layers.length - 1) {
    return layers;
  }
  const next = [...layers];
  const [layer] = next.splice(index, 1);
  if (!layer) {
    return layers;
  }
  next.splice(index + 1, 0, layer);
  return next;
}

async function loadImage(src: string): Promise<HTMLImageElement> {
  const image = new Image();
  image.crossOrigin = "anonymous";
  await new Promise<void>((resolve, reject) => {
    image.onload = () => resolve();
    image.onerror = () => reject(new Error(`Failed to load image: ${src}`));
    image.src = src;
  });
  return image;
}

function getContainDrawSize(image: HTMLImageElement): { width: number; height: number } {
  const sourceWidth = image.naturalWidth || image.width || ADMIN_CREATE_CANVAS_WIDTH;
  const sourceHeight = image.naturalHeight || image.height || ADMIN_CREATE_CANVAS_HEIGHT;
  if (sourceWidth <= 0 || sourceHeight <= 0) {
    return {
      width: ADMIN_CREATE_CANVAS_WIDTH,
      height: ADMIN_CREATE_CANVAS_HEIGHT,
    };
  }

  const containScale = Math.min(
    ADMIN_CREATE_CANVAS_WIDTH / sourceWidth,
    ADMIN_CREATE_CANVAS_HEIGHT / sourceHeight,
  );

  return {
    width: sourceWidth * containScale,
    height: sourceHeight * containScale,
  };
}

export async function exportAdminCreateCompositionToDataUrl(input: {
  baseTileUrl: string;
  layers: AdminCreateLayer[];
  canvas?: HTMLCanvasElement;
  imageLoader?: (src: string) => Promise<HTMLImageElement>;
}): Promise<string> {
  const canvas =
    input.canvas ??
    Object.assign(document.createElement("canvas"), {
      width: ADMIN_CREATE_CANVAS_WIDTH,
      height: ADMIN_CREATE_CANVAS_HEIGHT,
    });
  canvas.width = ADMIN_CREATE_CANVAS_WIDTH;
  canvas.height = ADMIN_CREATE_CANVAS_HEIGHT;
  const context = canvas.getContext("2d");
  if (!context) {
    throw new Error("2D canvas context is unavailable.");
  }

  const imageLoader = input.imageLoader ?? loadImage;
  const baseTile = await imageLoader(input.baseTileUrl);
  context.clearRect(0, 0, canvas.width, canvas.height);
  context.drawImage(baseTile, 0, 0, ADMIN_CREATE_CANVAS_WIDTH, ADMIN_CREATE_CANVAS_HEIGHT);

  for (const layer of input.layers) {
    const overlay = await imageLoader(layer.url);
    const drawSize = getContainDrawSize(overlay);
    context.save();
    context.globalAlpha = layer.opacity;
    context.translate(ADMIN_CREATE_CANVAS_WIDTH / 2 + layer.x, ADMIN_CREATE_CANVAS_HEIGHT / 2 + layer.y);
    context.scale(layer.scale, layer.scale);
    context.drawImage(
      overlay,
      -drawSize.width / 2,
      -drawSize.height / 2,
      drawSize.width,
      drawSize.height,
    );
    context.restore();
  }

  return canvas.toDataURL("image/png");
}
